import type { LeaveRequest, LeaveType, StaffProfile } from "../services/staffService";

export type LeaveFilters = {
  search: string;
  /** A status, "today" for people away today, or "" for everything. */
  status: string;
  leaveType: string;
  department: string;
  from: string;
  to: string;
};

export const EMPTY_LEAVE_FILTERS: LeaveFilters = {
  search: "",
  status: "",
  leaveType: "",
  department: "",
  from: "",
  to: "",
};

export const LEAVE_STATUS_TABS: [string, string][] = [
  ["", "All"],
  ["pending", "Pending"],
  ["today", "On leave today"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
  ["cancelled", "Cancelled"],
];

export const LEAVE_STATUS_DOTS: Record<string, string> = {
  pending: "bg-amber-500",
  approved: "bg-green-500",
  rejected: "bg-red-500",
  cancelled: "bg-gray-400",
};

/** Today as YYYY-MM-DD in the reader's own timezone. */
export function localToday(now = new Date()) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Calendar days from start to end, both included, as the server counts them. */
export function leaveDays(start: string, end: string) {
  if (!start || !end) return 0;
  const days = (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86400000 + 1;
  return Number.isFinite(days) && days > 0 ? days : 0;
}

const isAway = (leave: LeaveRequest, day: string) =>
  leave.status === "approved" && leave.start_date <= day && leave.end_date >= day;

/** Pending or approved leave for this person that shares a day with the dates given. */
export function overlappingLeave(
  leaves: LeaveRequest[],
  staffId: number | undefined,
  start: string,
  end: string,
  excludeId?: number,
) {
  if (!staffId || !start || !end) return [];
  return leaves.filter(
    (leave) =>
      leave.staff === staffId &&
      leave.id !== excludeId &&
      ["pending", "approved"].includes(leave.status) &&
      leave.start_date <= end &&
      leave.end_date >= start,
  );
}

/** Days of one leave type already approved (and still pending) for a person in a year. */
export function leaveBalance(
  leaves: LeaveRequest[],
  staffId: number | undefined,
  leaveType: LeaveType | undefined,
  year: number,
  excludeId?: number,
) {
  if (!staffId || !leaveType) return null;
  const inYear = leaves.filter(
    (leave) =>
      leave.staff === staffId &&
      leave.leave_type === leaveType.id &&
      leave.id !== excludeId &&
      leave.start_date.startsWith(String(year)),
  );
  const sum = (status: string) =>
    inYear.filter((leave) => leave.status === status).reduce((total, leave) => total + leave.total_days, 0);
  const used = sum("approved");
  const pending = sum("pending");
  return { allowed: leaveType.max_days_per_year, used, pending, left: leaveType.max_days_per_year - used - pending };
}

export function activeLeaveFilterCount(filters: LeaveFilters) {
  return Object.values(filters).filter((value) => value.trim()).length;
}

export function leaveStatusCounts(leaves: LeaveRequest[], today = localToday()) {
  const counts: Record<string, number> = { "": leaves.length, today: 0 };
  for (const leave of leaves) {
    counts[leave.status] = (counts[leave.status] ?? 0) + 1;
    if (isAway(leave, today)) counts.today += 1;
  }
  return counts;
}

/** Leave matching the filters: pending first, then the latest start date. */
export function filterLeaves(
  leaves: LeaveRequest[],
  filters: LeaveFilters,
  profiles: StaffProfile[] = [],
  { ignoreStatus = false, today = localToday() } = {},
) {
  const departmentOf = new Map(profiles.map((profile) => [profile.id, profile.department]));
  const term = filters.search.trim().toLowerCase();
  return leaves
    .filter((leave) => {
      const department = departmentOf.get(leave.staff);
      const matchesStatus =
        ignoreStatus ||
        !filters.status ||
        (filters.status === "today" ? isAway(leave, today) : leave.status === filters.status);
      return (
        matchesStatus &&
        (!term ||
          [leave.staff_name, leave.reason, leave.leave_type_name, leave.approved_by_name ?? ""].some((value) =>
            value.toLowerCase().includes(term),
          )) &&
        (!filters.leaveType || String(leave.leave_type) === filters.leaveType) &&
        (!filters.department ||
          (filters.department === "none" ? department == null : String(department ?? "") === filters.department)) &&
        // Any leave that touches the range, not only leave wholly inside it.
        (!filters.from || leave.end_date >= filters.from) &&
        (!filters.to || leave.start_date <= filters.to)
      );
    })
    .sort((a, b) => {
      const waiting = Number(b.status === "pending") - Number(a.status === "pending");
      return waiting || b.start_date.localeCompare(a.start_date) || b.id - a.id;
    });
}
